import React from 'react';
import { Card, Input, Select, Typography, Space, Row, Col, Badge, Upload, Button, message } from 'antd';
import { EnvironmentOutlined, UploadOutlined, DeleteOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

/** Sign types whose layout has an image frame the user can fill with a picture. */
const IMAGE_FRAME_TYPES = ['bilingual', 'trilingual', 'tourist_photo'];
/** Max upload size — images are embedded as base64 in the SVG, so keep it sane. */
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const ARROWS = [
  { label: '\u2191 Straight (0\u00b0)', value: 'up' },
  { label: '\u2197 Bear Right (45\u00b0)', value: 'up_right' },
  { label: '\u2192 Right (90\u00b0)', value: 'right' },
  { label: '\u2196 Bear Left (315\u00b0)', value: 'up_left' },
  { label: '\u2190 Left (270\u00b0)', value: 'left' },
];

const SYMBOLS = [
  { label: 'None', value: '' },
  { label: 'Airport', value: 'airport' },
  { label: 'Hospital', value: 'hospital' },
  { label: 'Railway', value: 'railway' },
  { label: 'Parking', value: 'parking' },
  { label: 'Fuel', value: 'fuel' },
  { label: 'Hotel', value: 'hotel' },
  { label: 'Restaurant', value: 'restaurant' },
  { label: 'Bus Station', value: 'bus' },
];

const SINGLE_BLOCK_TYPES = [
  'tourist_photo', 'bilingual', 'trilingual',
  'place_identification', 'supplementary', 'flag_type', 'right_finger_destination',
];
const NEEDS_NATIVE_TEXT = [
  'trilingual', 'map_type', 'map_type_advance', 'regulatory_advance',
  'reassurance', 'place_identification', 'truck_layby', 'toll_booth', 'weigh_bridge',
  'gantry', 'gantry_at_grade', 'gantry_flyover',
  'advance_interchange', 'advance_interchange_2', 'advance_interchange_3', 'advance_interchange_double',
  'lane_dedicated_gantry', 'lane_dedicated_gantry_single', 'grade_separated_advance', 'expressway_ahead',
];
const NEEDS_NUMBER_TEXT = ['regulatory_advance', 'reassurance'];

export default function DestinationPanel({ destinations, onChange, signType }) {
  const isSingleBlock = SINGLE_BLOCK_TYPES.includes(signType);
  const showNativeText = NEEDS_NATIVE_TEXT.includes(signType);
  const showNumberText = NEEDS_NUMBER_TEXT.includes(signType);
  const showImageUpload = IMAGE_FRAME_TYPES.includes(signType);
  const maxDest = isSingleBlock ? 1 : 3;
  const visibleDests = destinations.slice(0, maxDest);

  const updateDest = (index, field, value) => {
    const updated = destinations.map((d, i) =>
      i === index ? { ...d, [field]: value } : d
    );
    onChange(updated);
  };

  /** Read the chosen image as a base64 data URI and store it in the destination's
   *  image_path. Returns false so antd's Upload does not try to POST it anywhere. */
  const handleImageUpload = (index, file) => {
    if (!file.type || !file.type.startsWith('image/')) {
      message.error('Please choose an image file.');
      return false;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      message.error('Image too large (max 5 MB). Pick a smaller file.');
      return false;
    }
    const reader = new FileReader();
    reader.onload = () => updateDest(index, 'image_path', reader.result);
    reader.onerror = () => message.error('Could not read that image.');
    reader.readAsDataURL(file);
    return false;
  };

  const getBlockLabel = (index) => {
    if (isSingleBlock) return 'Main';
    const labels = ['Forward / Up', 'Left', 'Right'];
    return labels[index] || `Block ${index + 1}`;
  };

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Title level={5} style={{ margin: 0, marginBottom: 4 }}>
          <EnvironmentOutlined /> Destinations {isSingleBlock ? '(Single Block)' : '(Max 3 per sign)'}
        </Title>
        <Text type="secondary" style={{ fontSize: 11 }}>
          {isSingleBlock ? (
            'This sign type uses a single text block.'
          ) : (
            <>Forward destination at top • Left in middle • Right at bottom<br />
            2+ languages: English + Hindi/Local (Section 16.3.1)</>
          )}
        </Text>
      </div>

      {visibleDests.map((dest, index) => (
        <Card 
          key={index}
          size="small"
          className="dest-card"
          title={
            <Space>
              <Badge count={index + 1} style={{ backgroundColor: 'var(--accent)' }} />
              <Text strong style={{ fontSize: 12 }}>{getBlockLabel(index)}</Text>
            </Space>
          }
        >
          <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
            {!isSingleBlock && (
              <Row gutter={8}>
                <Col xs={24} sm={12}>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>Arrow Direction</Text>
                  <Select
                    value={dest.arrow_direction || (index === 0 ? 'up' : index === 1 ? 'left' : 'right')}
                    onChange={(value) => updateDest(index, 'arrow_direction', value)}
                    style={{ width: '100%' }}
                    size="large"
                    placeholder="Select arrow direction"
                  >
                    {ARROWS.map(a => (
                      <Select.Option key={a.value} value={a.value}>{a.label}</Select.Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12}>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>Symbol</Text>
                  <Select
                    value={dest.symbol || ''}
                    onChange={(value) => updateDest(index, 'symbol', value)}
                    style={{ width: '100%' }}
                    size="large"
                  >
                    {SYMBOLS.map(s => (
                      <Select.Option key={s.value} value={s.value}>{s.label}</Select.Option>
                    ))}
                  </Select>
                </Col>
              </Row>
            )}

            {/* Hindi + English row */}
            <Row gutter={8}>
              <Col xs={24} sm={isSingleBlock ? 24 : 12}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>Hindi (हिंदी)</Text>
                <Input
                  placeholder="हिंदी में लिखें"
                  value={dest.hindi_text}
                  onChange={(e) => updateDest(index, 'hindi_text', e.target.value)}
                  size="large"
                />
              </Col>
              <Col xs={24} sm={isSingleBlock ? 24 : 12}>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>English</Text>
                <Input
                  placeholder="Enter in English"
                  value={dest.english_text}
                  onChange={(e) => updateDest(index, 'english_text', e.target.value)}
                  size="large"
                />
              </Col>
              {!isSingleBlock && (
                <Col xs={24} sm={24}>
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>Distance (km)</Text>
                  <Input
                    placeholder="e.g. 45"
                    value={dest.distance_km}
                    onChange={(e) => updateDest(index, 'distance_km', e.target.value)}
                    size="large"
                  />
                </Col>
              )}
            </Row>

            {/* Image frame upload (bilingual / trilingual / tourist photo) */}
            {showImageUpload && (
              <div>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>
                  Frame Image (fills the center panel)
                </Text>
                {dest.image_path ? (
                  <Space align="center">
                    <img
                      src={dest.image_path}
                      alt="frame preview"
                      style={{ width: 64, height: 64, objectFit: 'cover', border: '1px solid #d9d9d9', borderRadius: 4 }}
                    />
                    <Button
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => updateDest(index, 'image_path', '')}
                    >
                      Remove
                    </Button>
                  </Space>
                ) : (
                  <Upload
                    accept="image/*"
                    showUploadList={false}
                    beforeUpload={(file) => handleImageUpload(index, file)}
                  >
                    <Button icon={<UploadOutlined />} size="large">Upload image</Button>
                  </Upload>
                )}
              </div>
            )}

            {/* Native text row */}
            {showNativeText && (
              <div>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>Native / Local Language</Text>
                <Input
                  placeholder="e.g. తెలుగు / தமிழ் / ಕನ್ನಡ"
                  value={dest.native_text || ''}
                  onChange={(e) => updateDest(index, 'native_text', e.target.value)}
                  size="large"
                />
              </div>
            )}

            {/* Number text row */}
            {showNumberText && !isSingleBlock && (
              <div>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>Number / Distance Text</Text>
                <Input
                  placeholder="e.g. 70"
                  value={dest.number_text || ''}
                  onChange={(e) => updateDest(index, 'number_text', e.target.value)}
                  size="large"
                  style={{ width: 120 }}
                />
              </div>
            )}
          </Space>
        </Card>
      ))}

      {/* Sign-type specific notes */}
      <Card size="small" className="irc-note">
        <Title level={5} style={{ margin: 0, marginBottom: 8, color: 'var(--teal)' }}>IRC:67-2022 Guidelines</Title>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {isSingleBlock ? (
            <>
              <li><Text type="secondary" style={{ fontSize: 11 }}>Single block sign - one destination/information</Text></li>
              {signType === 'bilingual' && <li><Text type="secondary" style={{ fontSize: 11 }}>Fixed dimensions: 900mm x 1200mm (Blue background)</Text></li>}
              {signType === 'trilingual' && <li><Text type="secondary" style={{ fontSize: 11 }}>Fixed dimensions: 900mm x 1200mm (Blue background, 3 languages)</Text></li>}
              {signType === 'tourist_photo' && <li><Text type="secondary" style={{ fontSize: 11 }}>Tourist sign with photograph (Black background)</Text></li>}
            </>
          ) : (
            <>
              <li><Text type="secondary" style={{ fontSize: 11 }}>Section 16.3.2.4: Arrow and panel centered vertically</Text></li>
              <li><Text type="secondary" style={{ fontSize: 11 }}>Section 16.3.2.5: Max 3 destinations, 2 languages</Text></li>
              <li><Text type="secondary" style={{ fontSize: 11 }}>Arrows at 22.5° increments allowed</Text></li>
              <li><Text type="secondary" style={{ fontSize: 11 }}>Forward: top, Left: middle, Right: bottom</Text></li>
              {showNativeText && <li><Text type="secondary" style={{ fontSize: 11 }}>Native/local language text is placed as the topmost text in each block</Text></li>}
              {showNumberText && <li><Text type="secondary" style={{ fontSize: 11 }}>Number text (distance) appears alongside regulatory elements</Text></li>}
            </>
          )}
        </ul>
      </Card>
    </Space>
  );
}
